import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import {
    Autocomplete,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Typography,
} from "@mui/material";
import InvoiceWithTotalsTable from "../../components/InvoiceWithTotalsTable";
import useProviderService from '../../hooks/useProviderService';
import useBuildingService from '../../hooks/useBuildingService';
import { handleApiError, DatePickerComponent } from '../../components/Utils';

function ListInvoiceDrafts() {
    const { getProviders } = useProviderService();
    const { getBuildings } = useBuildingService();

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const [apply, setApply] = useState(false);
    const [reset, setReset] = useState(false);

    const [externalProviderId, setExternalProviderId] = usePersistedState('list_invoice_drafts_externalProviderId', null);
    const debouncedExternalProviderId = useDebounce(externalProviderId);
    const [startInvoiceDate, setStartInvoiceDate] = useState(null);
    const [endInvoiceDate, setEndInvoiceDate] = useState(null);

    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const isFirstFetch = useRef(true);

    const fetchProviders = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            const result = await getProviders({});
            setProviders(result.data.items);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar los proveedores.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchBuildings = useCallback(async () => {
        try {
            const result = await getBuildings({});
            setBuildings(result.data.items);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las obras.'));
        }
    }, []);

    useEffect(() => {
        fetchProviders();
        fetchBuildings();
    }, []);

    useEffect(() => {
        setApply(true);
        setTimeout(() => setApply(false), 0);
    }, [debouncedExternalProviderId, selectedProvider, selectedBuilding, startInvoiceDate, endInvoiceDate]);

    const handleResetFilters = () => {
        setSelectedProvider(null);
        setSelectedBuilding(null);
        setExternalProviderId(null);
        setReset(true);
        setStartInvoiceDate(null);
        setEndInvoiceDate(null);
        setTimeout(() => setReset(false), 0);
    };

    const handleProviderChange = (event, newValue) => {
        setSelectedProvider(newValue);
    };

    const handleBuildingChange = (event, newValue) => {
        setSelectedBuilding(newValue);
    };

    const handleDateChange = (newValue, name) => {
        if (name === 'start') {
            setStartInvoiceDate(newValue);
        } else if (name === 'end') {
            setEndInvoiceDate(newValue);
        }
    };

    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    {/* Filters */}
                    <Grid container spacing={1} alignItems="center">
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={9}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item xs={3} sm={2}>
                                        <TextField
                                            fullWidth
                                            label="Código externo"
                                            name="external_provider_id"
                                            value={externalProviderId || ""}
                                            onChange={(e) => setExternalProviderId(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item sm={2}>
                                        <Autocomplete
                                            options={buildings}
                                            getOptionLabel={(option) => option.code?.toString() + '-' + option.name?.toString()}
                                            value={selectedBuilding}
                                            onChange={handleBuildingChange}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Obra" style={{ maxWidth: '100%' }} />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item sm={2}>
                                        <Autocomplete
                                            options={providers}
                                            getOptionLabel={(option) => option.code?.toString() + '-' + option.name?.toString()}
                                            value={selectedProvider}
                                            onChange={handleProviderChange}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Proveedor" style={{ maxWidth: '100%' }} />
                                            )}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Botones alineados a la derecha */}
                            <Grid item xs={12} md={3}>
                                <Grid container spacing={1} justifyContent="flex-end">
                                    <Grid item>
                                        <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                            Limpiar filtros
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Filtros de Fecha */}
                        <Grid container spacing={1} alignItems="center" sx={{ marginTop: '10px' }}>
                            <Grid item xs={12} md={9}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <DatePickerComponent
                                            label="Fecha de Inicio"
                                            value={startInvoiceDate}
                                            onChange={(newValue) => handleDateChange(newValue, 'start')}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <DatePickerComponent
                                            label="Fecha de Fin"
                                            value={endInvoiceDate}
                                            onChange={(newValue) => handleDateChange(newValue, 'end')}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <br />
                    <br />
                    {/* Borradores de facturas de proveedor */}
                    <InvoiceWithTotalsTable
                        title={'Borradores de facturas de proveedor'}
                        isClient={false}
                        draftsMode={true}
                        externalProviderId={debouncedExternalProviderId}
                        selectedBuilding={selectedBuilding}
                        selectedProvider={selectedProvider}
                        startInvoiceDate={startInvoiceDate}
                        endInvoiceDate={endInvoiceDate}
                        apply={apply}
                        reset={reset}
                    />
                </>
            )}
            <br />
            {error && (
                <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                    {error}
                </Typography>
            )}
        </div>
    );
}

export default ListInvoiceDrafts;
